import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type KeyboardEvent } from "react";
import { VOICE_MAX_DURATION_MS } from "@scrile/contracts";
import { Icon } from "../components/Icon";
import { t, type MessageKey } from "../i18n";
import { formatClock } from "../lib/format";
import { readItem, removeItem, writeItem } from "../lib/safeStorage";
import { canRecord, checkVoice, recorderEnv, recorderErrorCode, VoiceRecorder, type VoiceRecording } from "./recorder";

/**
 * Поле ввода: текст (Enter — отправить, Shift+Enter — перенос; во время набора через
 * IME Enter принадлежит подсказке, а не отправке) и голосовое — запись в браузере.
 *
 * Пока текста нет — кнопка микрофона, есть — «отправить», как в мессенджерах. Счётчик
 * знаков появляется только у лимита: постоянные «1987 left» — шум. Черновик — в
 * sessionStorage вкладки: обновление страницы не съедает недописанное.
 */

export interface ComposerHandle {
  focus(): void;
}

export interface ComposerProps {
  heroName: string;
  heroSlug: string;
  userId: string;
  maxLength: number;
  onSendText(text: string): void;
  onSendVoice(recording: VoiceRecording): void;
  /** Показать короткое сообщение (ошибка микрофона и т. п.). */
  onNotice(text: string): void;
  /** Поле выросло или сжалось — лента держит низ. */
  onResize?(): void;
  /** Сверху над полем: «Slow down a little», «Reconnecting…». */
  notice?: string | null;
}

/** Сколько знаков до лимита, когда пора показать счётчик. */
const COUNTER_FROM = 200;
const MAX_LINES_PX = 148;

export const Composer = forwardRef<ComposerHandle, ComposerProps>(function Composer(props, ref) {
  const { heroName, heroSlug, userId, maxLength, onSendText, onSendVoice, onNotice, onResize, notice } = props;
  const draftKey = `scrile.chat.draft:${userId}:${heroSlug}`;
  const [text, setText] = useState(() => readItem("session", draftKey) ?? "");
  const [recording, setRecording] = useState<{ startedAt: number } | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [starting, setStarting] = useState(false);
  const input = useRef<HTMLTextAreaElement>(null);
  const recorder = useRef<VoiceRecorder | null>(null);
  const voiceAvailable = canRecord(recorderEnv());

  useImperativeHandle(ref, () => ({ focus: () => input.current?.focus() }), []);

  // Высота поля — по содержимому, до пяти строк; дальше — прокрутка внутри
  useEffect(() => {
    const element = input.current;
    if (!element) return;
    element.style.height = "auto";
    element.style.height = `${Math.min(MAX_LINES_PX, element.scrollHeight)}px`;
    onResize?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  useEffect(() => {
    if (text) writeItem("session", draftKey, text);
    else removeItem("session", draftKey);
  }, [text, draftKey]);

  useEffect(() => {
    if (!recording) return;
    const timer = setInterval(() => setElapsed(performance.now() - recording.startedAt), 200);
    return () => clearInterval(timer);
  }, [recording]);

  // Ушли с экрана посреди записи — микрофон выключить
  useEffect(() => () => { recorder.current?.cancel(); recorder.current = null; }, []);

  const trimmed = text.trim();
  const left = maxLength - text.length;

  function submit() {
    if (!trimmed || text.length > maxLength) return;
    onSendText(trimmed);
    setText("");
    input.current?.focus();
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing || event.keyCode === 229) return;
    event.preventDefault();
    submit();
  }

  async function startRecording() {
    if (recorder.current) return;
    const next = new VoiceRecorder({ onLimit: () => void finishRecording(true) });
    recorder.current = next;
    setStarting(true);
    try {
      await next.start();
      if (recorder.current !== next) return;
      setElapsed(0);
      setRecording({ startedAt: performance.now() });
    } catch (error) {
      if (recorder.current !== next) return;
      recorder.current = null;
      const code = recorderErrorCode(error);
      onNotice(t(code === "denied" ? "messenger.voice.denied" : code === "unsupported" ? "messenger.voice.unsupported" : "messenger.voice.failed"));
    } finally {
      setStarting(false);
    }
  }

  async function finishRecording(atLimit = false) {
    const active = recorder.current;
    recorder.current = null;
    setRecording(null);
    if (!active) return;
    const result = await active.stop();
    if (!result) return;
    const problem = checkVoice({ size: result.blob.size, durationMs: result.durationMs, mime: result.mime });
    if (problem) {
      onNotice(t(`messenger.voice.${problem}` as MessageKey));
      return;
    }
    if (atLimit) onNotice(t("messenger.recordLimit"));
    onSendVoice(result);
  }

  function cancelRecording() {
    recorder.current?.cancel();
    recorder.current = null;
    setRecording(null);
  }

  return (
    <div className="composer">
      {notice && (
        <p className="composer__notice" role="status">
          {notice}
        </p>
      )}
      {recording ? (
        <div className="composer__row composer__row--recording">
          <button type="button" className="icon-btn icon-btn--quiet" onClick={cancelRecording} aria-label={t("messenger.recordCancel")}>
            <Icon name="trash" />
          </button>
          <div className="recording" role="timer" aria-live="off">
            <span className="recording__dot" aria-hidden="true" />
            <span className="recording__label">{t("messenger.recordingNow")}</span>
            <span className="recording__time">
              {formatClock(elapsed / 1000)} <span className="muted">/ {formatClock(VOICE_MAX_DURATION_MS / 1000)}</span>
            </span>
            <span className="recording__bar" aria-hidden="true">
              <i style={{ width: `${Math.min(100, (elapsed / VOICE_MAX_DURATION_MS) * 100)}%` }} />
            </span>
          </div>
          <button type="button" className="icon-btn icon-btn--accent composer__send" onClick={() => void finishRecording()} aria-label={t("messenger.recordSend")}>
            <Icon name="send" />
          </button>
        </div>
      ) : (
        <div className="composer__row">
          <label className="composer__field">
            <span className="sr-only">{t("messenger.inputLabel", { hero: heroName })}</span>
            <textarea
              ref={input}
              className="composer__input"
              rows={1}
              value={text}
              maxLength={maxLength}
              placeholder={t("messenger.placeholder", { hero: heroName })}
              enterKeyHint="send"
              autoComplete="off"
              onChange={(event) => setText(event.target.value)}
              onKeyDown={onKeyDown}
            />
            {left <= COUNTER_FROM && (
              <span className={`composer__counter${left <= 0 ? " composer__counter--limit" : ""}`} aria-live="polite">
                {left <= 0 ? t("messenger.tooLong", { max: maxLength }) : t("messenger.charsLeft", { count: left })}
              </span>
            )}
          </label>
          {trimmed || !voiceAvailable ? (
            <button
              type="button"
              className="icon-btn icon-btn--accent composer__send"
              onClick={submit}
              disabled={!trimmed || text.length > maxLength}
              aria-label={t("messenger.send")}
            >
              <Icon name="send" />
            </button>
          ) : (
            <button type="button" className="icon-btn composer__mic" disabled={starting} onClick={() => void startRecording()} aria-label={t("messenger.record")}>
              <Icon name="mic" />
            </button>
          )}
        </div>
      )}
    </div>
  );
});
